import { useNavigate } from 'react-router-dom';
import { Button } from '../../../../core/components/atoms/Button';
import { Icon } from '../../../../core/components/atoms/Icon';
import { Typography } from '../../../../core/components/molecules/Typography';
import {
  IconDone,
  IconFav,
} from '../../../../core/components/organism/ContentCard/variants';
import { Tag } from '../../../../core/components/atoms/Tag';
import { Colors } from '../../../../core/models/disciplines';
import { IResponseFavorite, useContentFavorite } from '../../hooks/useContentFavorite';

interface ILessonCard {
  lesson: any;
  slug: string;
  discipline: string;
  module: string;
  subject: string;
  subjectSlug: string;
  done: boolean;
  favorite: boolean;
  onDone?: (id: string, slug: string) => void;
  onClose: () => void;
  callback: (data: IResponseFavorite) => void;
  locked?: boolean;
  onLockedClick?: () => void;
  freemiumUnlocked?: boolean;
  doneLoading?: boolean;
}

const formatDuration = (duration: number) => {
  const totalSeconds = Math.floor(duration);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const minutesString = minutes.toString().padStart(2, '0');
  const secondsString = seconds.toString().padStart(2, '0');

  if (hours > 0) {
    const hoursString = hours.toString().padStart(2, '0');
    return `${hoursString}:${minutesString}:${secondsString}`;
  }
  return `${minutesString}:${secondsString}`;
};

function LessonCard({
  lesson,
  discipline,
  module,
  subject,
  subjectSlug,
  slug,
  done,
  favorite,
  onDone,
  onClose,
  callback,
  locked = false,
  onLockedClick,
  freemiumUnlocked = false,
  doneLoading,
}: ILessonCard) {
  const navigate = useNavigate();
  const { mutate, isLoading } = useContentFavorite(true, callback);

  const selected = lesson.slug === slug;
  const selectedBorder = selected
    ? 'border-hairline border-brand-color-primary-pure'
    : null;

  const replacementLink = `/disciplinas/${discipline}/modulo/${module}/assunto/${subjectSlug}/videoaula/${lesson.slug}`;

  const onFavorite = () => {
    mutate({
      category: 'lesson',
      targetId: lesson._id,
      targetType: 'Lesson',
    })
  }

  return (
    <div
      key={lesson._id}
      className={'flex cursor-pointer rounded-md bg-color-lightest p-4 shadow-level-1 '+selectedBorder}
    >
      <div
        className='flex flex-1 flex-col gap-sm'
        onClick={() => {
          if (locked) {
            onLockedClick?.();
            return;
          }
          if (!selected) {
            onClose();
            navigate(replacementLink);
          }
        }}
      >
        <div className='flex flex-row justify-between items-center'>
          <div className='flex items-center gap-sm'>
            <Icon
              Icon='play_circle'
              type='material'
              className='text-md text-brand-color-secondary-pure'
            />
            <Typography size='h6' weight='bold' type='titleSection'>
              VIDEOAULA
            </Typography>
          </div>
          {subject && (
            <Tag
              variant={discipline as Colors}
              className='flex flex-row items-center gap-sm px-2 mr-3 max-w-[180px] min-w-0'
              title={subject}
            >
              <Typography size='small' className='text-[#fff] truncate'>
                {subject}
              </Typography>
            </Tag>
          )}
        </div>
        <Typography type='title' weight='bold'>
          {lesson.title}
        </Typography>
        <Typography size='small'>
          {formatDuration(lesson.duration) || '--:--'}{' '}
          {lesson.teacher?.length ? `Prof. ${lesson.teacher[0]?.name}` : ''}
        </Typography>
      </div>
      {locked ? (
        <div className='flex w-fit flex-col items-center justify-center'>
          <Icon
            id='lock-icon'
            Icon='lock'
            variant='rounded'
            type='material'
            className='text-xxl filled text-mono-color-medium'
          />
        </div>
      ) : (
        <div className='flex w-fit flex-col items-end justify-between'>
          <Button
            variant='text'
            size='regular'
            className='px-0 py-0'
            loading={doneLoading ?? isLoading}
            onClick={() => onDone && onDone(lesson._id, lesson.slug)}
          >
            <Icon
              Icon='check_circle'
              type='material'
              className={IconDone({ type: done ? 'done' : 'unseen' })}
            />
          </Button>
          {freemiumUnlocked ? (
            <Icon
              id='freemium-unlocked-icon'
              Icon='lock_open'
              variant='rounded'
              type='material'
              className='text-xxl filled text-feedback-color-success-pure'
            />
          ) : (
            <Button
              onlyIcon
              variant='text'
              size='regular'
              className='px-0 py-0'
              loading={isLoading}
              onClick={() => onFavorite()}
            >
              <Icon
                Icon='star'
                type='material'
                variant='rounded'
                className={IconFav({ type: favorite ? 'favorite' : 'notFavorite' })}
              />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default LessonCard;
